import { Component, signal } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { PropertyService } from '../../../_services/properties/property.service';
import { ToastService } from '../../../_services/toast.service';
import { InrShortPipe } from '../../../_pipes/indian-currency.pipe';
import { CommonModule, Location } from '@angular/common';
import { RouterModule, Router } from '@angular/router';
import { PropertySearchPipe } from '../../../_pipes/property-search.pipe';
import { DashboardService } from '../../../_services/dashboard.service';
import { LoaderService } from '../../../_services/loader.service';
import { environment } from '../../../../environments/environment';
import { HttpClient, HttpParams } from '@angular/common/http';
import { CiService } from '../../../_services/ci.service';
interface Property {
  _id: string;
  name: string;
  builder: string;
  price: any;
  priceUnit: string;
  propertyType: string;
  bedrooms: number;
  landArea: number;
  location: string;
  shortDescription: string;
  isSoldOut: boolean;
  isLiked: boolean;
  galleryImage: any;
  ciIntelligence?: { status?: string };
  ciRecord?: { status?: string };
}

type User = {
  _id: string;
  name: string;
  email: string;
  phone: string;
  otp: string | null;
  password: string;
  otpVerified: boolean;
  role: any;
};

@Component({
  selector: 'app-slider-proprties',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    PropertySearchPipe,
  ],
  providers: [InrShortPipe],
  templateUrl: './slider-proprties.component.html',
  styleUrl: './slider-proprties.component.scss',
  standalone: true,
})
export class SliderProprtiesComponent {
  isAssignAdvisor = signal(false);
  isGenerateLink = signal(false);
  userRequest: any = signal([]);
  userDetails: any = {};
  searchTerm: string = '';
  filterForm!: FormGroup;
  properties: Property[] = [];
  allProperties: Property[] = [];
  filteredProperties: Property[] = [];
  sliderProperty: Property[] = [];
  selectedPropertyIds = new Set<string>();
  /** A slider takes exactly this many properties. */
  readonly sliderLimit = 5;
  /** Most properties one CI template can be downloaded for. */
  readonly maxSelection = 50;
  page: number = 1;
  limit: number = 20;
  constructor(
    private fb: FormBuilder,
    private readonly propertyService: PropertyService,
    private _NS: ToastService,
    private location: Location,
    private inrShortPipe: InrShortPipe,
    private router: Router,
    private _DS: DashboardService,
    private _LS:LoaderService,
    private http: HttpClient,
    private _CI: CiService,
  ) {}
  ngOnInit(): void {
    this.loadCiStatuses();
    this.initFilterForm();
    this.getAllProperties(this.page, this.limit);
    this.filterForm.valueChanges.subscribe(() => {
      this.applyFilters();
    });
  }
  private generatedCiPropertyIds = new Set<string>();
  private generatedCiPropertyNames = new Set<string>();
  private ciStatusesLoaded = false;
  private ciStatusesFailed = false;

  /** One shared lookup for the table, including reports not linked to properties yet. */
  private loadCiStatuses(page = 1): void {
    this._CI.getDashboardData(page === 1 ? undefined : { page }).subscribe({
      next: (dashboard) => {
        for (const record of dashboard.recentIntel ?? []) {
          if (record.status !== 'Completed') continue;
          if (record.propertyId) {
            this.generatedCiPropertyIds.add(record.propertyId);
          } else if (record.property) {
            this.generatedCiPropertyNames.add(record.property.trim().toLowerCase());
          }
        }
        if (dashboard.pagination && dashboard.pagination.page < dashboard.pagination.totalPages) {
          this.loadCiStatuses(dashboard.pagination.page + 1);
        } else {
          this.ciStatusesLoaded = true;
        }
      },
      error: () => { this.ciStatusesFailed = true; },
    });
  }

  ciGenerationStatus(property: Property): string {
    if (property.ciIntelligence?.status === 'GENERATED'
      || property.ciRecord?.status === 'GENERATED'
      || this.generatedCiPropertyIds.has(property._id)
      || this.generatedCiPropertyNames.has((property.name ?? '').trim().toLowerCase())) {
      return 'Generated';
    }
    if (this.ciStatusesFailed) return 'Unavailable';
    return this.ciStatusesLoaded ? 'Not Generated' : 'Checking…';
  }

  private initFilterForm(): void {
    this.filterForm = this.fb.group({
      name: [''],
      location: [''],
      bhk: [''],
      minPrice: [''],
      maxPrice: [''],
    });
  }
  resetFilters(): void {
    this.filterForm.reset({
      name: '',
      location: '',
      bhk: '',
      minPrice: '',
      maxPrice: '',
    });

    this.filteredProperties = [...this.allProperties];
  }
  private getAllProperties(page: number, limit: number): void {
    this.propertyService.properties(page, limit).subscribe((res: any) => {
      this.properties = res?.data || [];
      this.meta = res.meta;
      this.allProperties = [...this.properties]; // master list
      this.filteredProperties = [...this.properties]; // initial render
      const combined = [...this.sliderProperty, ...this.properties];
      this.sliderProperty = Array.from(
        new Map(combined.map((item:any) => [item._id, item])).values()
      ); // initial render
      this.getPageNumbers();
    });
  }

  formatPriceDisplay(priceData: any): string {
    if (!priceData) return 'N/A';
    if (Array.isArray(priceData)) {
      if (priceData.length === 0) return 'N/A';
      const values = priceData
        .map((p) => Number(p.price))
        .filter((v) => !isNaN(v));
      if (values.length === 0) return 'N/A';

      const min = Math.min(...values);
      const max = Math.max(...values);
      if (values.length === 1 || min === max) {
        return this.inrShortPipe.transform(min);
      }
      return `${this.inrShortPipe.transform(
        min
      )} - ${this.inrShortPipe.transform(max)}`;
    }
    return this.inrShortPipe.transform(priceData);
  }
  getBHKAndPriceRange(priceArray: any[]) {
    if (!priceArray || priceArray.length === 0) {
      return { bhk: 'N/A', price: 'Price on Request' };
    }
    const bhkValues = priceArray
      .map((p) => parseFloat(p.bhkType))
      .sort((a, b) => a - b);

    const minBhk = bhkValues[0];
    const maxBhk = bhkValues[bhkValues.length - 1];
    const priceValues = priceArray.map((p) => p.price).sort((a, b) => a - b);

    const minPrice = priceValues[0];
    const maxPrice = priceValues[priceValues.length - 1];
    return {
      bhkRange:
        minBhk === maxBhk ? `${minBhk} BHK` : `${minBhk} - ${maxBhk} BHK`,
      priceRange:
        minPrice === maxPrice
          ? this.inrShortPipe.transform(minPrice)
          : `${this.inrShortPipe.transform(
              minPrice
            )} - ${this.inrShortPipe.transform(maxPrice)}`,
    };
  }
  viewDetails(property: Property): void {
    this.router.navigate(['admin/property/details'], {
      queryParams: {
        id: property._id,
        mode: 'details',
      },
    });
  }

  navigateToGallery(property: Property): void {
    this.router.navigate(['admin/property/image-upload', property._id], {
      queryParams: {
        mode: 'gallery',
      },
    });
  }
  isPropertySelected(id: string): boolean {
    return this.selectedPropertyIds.has(id);
  }
  togglePropertySelection(id: string) {
    if (this.selectedPropertyIds.has(id)) {
      this.selectedPropertyIds.delete(id);
      return;
    }

    if (this.selectedPropertyIds.size >= this.maxSelection) {
      this._NS.error(`You can select up to ${this.maxSelection} properties.`);
      return;
    }

    this.selectedPropertyIds.add(id);
  }
  toggleSelectAll(event: any) {
    const isChecked = event.target.checked;
    if (isChecked) {
      for (const p of this.filteredProperties) {
        if (this.selectedPropertyIds.size >= this.maxSelection) {
          this._NS.error(`Only the first ${this.maxSelection} properties were selected.`);
          event.target.checked = false;
          break;
        }
        this.selectedPropertyIds.add(p._id);
      }
    } else {
      this.selectedPropertyIds.clear();
    }
  }
  isAllSelected(): boolean {
    return (
      this.filteredProperties.length > 0 &&
      this.selectedPropertyIds.size === this.filteredProperties.length
    );
  }

  applyFilters(): void {
    const { name, location, bhk, minPrice, maxPrice } = this.filterForm.value;

    const searchName = name?.toLowerCase().trim();
    const searchLocation = location?.toLowerCase().trim();

    this.filteredProperties = this.allProperties.filter((property) => {
      const propertyName = property.name?.toLowerCase() || '';
      const propertyLocation = (property.location || '')
        .toString()
        .toLowerCase();

      const matchName = !searchName || propertyName.includes(searchName);

      const matchLocation =
        !searchLocation || propertyLocation.includes(searchLocation);

      const matchBhk = !bhk || property.bedrooms === Number(bhk);

      const matchMinPrice = !minPrice || property.price >= Number(minPrice);

      const matchMaxPrice = !maxPrice || property.price <= Number(maxPrice);

      return (
        matchName && matchLocation && matchBhk && matchMinPrice && matchMaxPrice
      );
    });
  }

  generatedLink: string = '';
  isLoading: boolean = false;
  isCopied: boolean = false;

  get selectedData() {
    return this.sliderProperty.filter((p) =>
      this.selectedPropertyIds.has(p._id)
    );
  }

  get isSelectionValid(): boolean {
    return (
      this.selectedPropertyIds.size > 0 && this.selectedPropertyIds.size <= this.sliderLimit
    );
  }

  createDashToken() {
    if (this.selectedPropertyIds.size < this.sliderLimit) {
      this._NS.error(`Please select at least ${this.sliderLimit} properties.`);
      return;
    }

    if (this.selectedPropertyIds.size > this.sliderLimit) {
      this._NS.error(`A slider can have only ${this.sliderLimit} properties.`);
      return;
    }

    this.isGenerateLink.set(true);
  }

  async generateShareLink() {
    if (!this.isSelectionValid) return;
    this.isLoading = true;
    let dashboardProp: any = [];
    dashboardProp = this.selectedData.map((_: any) => {
      return { name: _.name, id: _._id };
    });
    const payload = {
      prop_limt: 5,
      dashboard_Prop: dashboardProp,
    };
    try {
      this._DS.createToken(payload).subscribe((res: any) => {
        if (res.status) {
          this._NS.success(
            'Carousel created successfully for the selected properties.'
          );
          this.closeSharePropertyModal();
        } else {
          this._NS.error('Failed to create carousel. Please try again.');
          this.closeSharePropertyModal();
        }
      });
    } catch (err) {
      console.error('Link generation error', err);
    } finally {
      this.isLoading = false;
    }
  }
  ciTemplateLoading = false;

  /** CI bulk-upload template prefilled with the selected properties. */
  downloadCiTemplate(): void {
    if (!this.selectedPropertyIds.size || this.ciTemplateLoading) return;
    this.ciTemplateLoading = true;
    this._CI.downloadPrefilledBulkTemplate([...this.selectedPropertyIds]).subscribe({
      next: (blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'ci-bulk-upload-selected-properties.xlsx';
        a.click();
        URL.revokeObjectURL(url);
        this.ciTemplateLoading = false;
        this.selectedPropertyIds.clear();
      },
      error: (err) => {
        this._NS.error(err?.message ?? 'Could not download the CI template.');
        this.ciTemplateLoading = false;
      },
    });
  }

  /** Keeps every listed property known so selections from any page/search show as chips. */
  private rememberProperties(list: Property[]): void {
    const combined = [...this.sliderProperty, ...list];
    this.sliderProperty = Array.from(new Map(combined.map((item) => [item._id, item])).values());
  }

  closeSharePropertyModal(): void {
    this.isGenerateLink.set(false);
    this.selectedPropertyIds.clear();
  }
  meta: any = {};

  // Expose Math to template
  Math = Math;

  getPageNumbers(): number[] {
    const { page, totalPages } = this.meta;
    const pages: number[] = [];

    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    pages.push(1);

    if (page > 3) pages.push(-1); // left dots

    for (
      let i = Math.max(2, page - 1);
      i <= Math.min(totalPages - 1, page + 1);
      i++
    ) {
      pages.push(i);
    }

    if (page < totalPages - 2) pages.push(-1); // right dots

    pages.push(totalPages);

    return pages;
  }

  onPageChange(page: number): void {
    if (page < 1 || page > this.meta.totalPages) return;
    this.getAllProperties(page, this.limit);
  }

  onLimitChange(event: Event): void {
    let page = 1,
      limit = Number((event.target as HTMLSelectElement).value);
    this.getAllProperties(page, limit);
  }
  search(){
    this._LS.show()
    this.propertyService.properties(1, 2000).subscribe((res: any) => {
      this.properties = res?.data || [];
      this.meta = res.meta;
      this.allProperties = [...this.properties]; // master list
      this.filteredProperties = [...this.properties]; // initial render
      const combined = [...this.sliderProperty, ...this.properties];
      this.sliderProperty = Array.from(
        new Map(combined.map((item:any) => [item._id, item])).values()
      ); // initial render
      this.getPageNumbers();
      this._LS.hide()
    });
  }
  searchTimeout: any;

  onSearchChange() {
    clearTimeout(this.searchTimeout);
    this.searchTimeout = setTimeout(() => {
      this.page = 1;
      this.getAllPropertiesTo(1);
    }, 400);

  }

  getAllPropertiesTo(page: number = 1) {

    const params = new HttpParams()
      .set('page', page)
      .set('limit', this.limit)
      .set('search', this.searchTerm?.trim() || '');

    this.http.get(`${environment.api_URL}/property`, { params })
      .subscribe((res: any) => {

        if (res?.status) {

          this.properties = (res.data || []).sort((a: any, b: any) => {
            return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
          });

          this.filteredProperties = [...this.properties];
          this.rememberProperties(this.properties);

          this.meta = res.meta || {};
          this.page = this.meta.page;
          // this.totalPages = this.meta.totalPages;
          this.getPageNumbers()
        }

      });
  }
  searchItem(){
    // this.loader.show()
    this.propertyService.searchProperty(this.searchTerm).subscribe({
      next: (response:any) => {
        if(response.data.length){
          this.properties = response.data || [];
          this.filteredProperties = [...this.properties];
          this.rememberProperties(this.properties);
          this.meta = response.meta || {};
          this.page = this.meta.page;
          // this.totalPages = this.meta.totalPages;
          this.getPageNumbers()
        } else{
          this.getAllProperties(1,20);
        }
        // this.loader.hide()
      },
      error: (err:any) => {
        // this.showDeleteModal = false;
        this._NS.error(err.message);
        // this.loader.hide()
      },
    });
}
getStartIndex(meta: any): number {
  const page = +meta?.page || 1;
  const limit = +meta?.limit || 0;

  return Math.max(1, (page - 1) * limit + 1);
}
getEndIndex(meta: any): number {
  const page = +meta?.page || 1;
  const limit = +meta?.limit || 0;
  const total = +meta?.total || 0;

  return Math.min(page * limit, total);
}
}
